import { components as c } from "@/components";
import {
    BuilderFormSelectDialog,
    BuilderScheduleSelectDialog,
} from "@/features/builder/components/BuilderSelectDialogs";
import { filterBuilderPickerItems } from "@/features/builder/utils/builderAccess";
import { NextButton } from "@/features/i18n/formComponents";
import { useSelectFormsLite } from "@/features/forms/api/selectForms";
import { useAuth } from "@/utils/auth";
import { useSchedulesQuery } from "@/features/schedules/api/selectSchedules";
import { useTrackerMutation } from "@/features/trackers/api/upsertTracker";
import { useUpdateTracker } from "@/features/trackers/api/updateTracker";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { templateSettingsSchema } from "@patient-watch/types";
import { useForm } from "react-hook-form";
import { z } from "zod";

const searchSchema = z.object({
    shared_attribute_id: z.string().optional(),
});

const TrackerForm = () => {
    const { diaryId, trackerId } = Route.useParams();
    const { shared_attribute_id: existingSharedAttributeId } = Route.useSearch();
    const { data: forms = [] } = useSelectFormsLite();
    const { data: schedules = [] } = useSchedulesQuery();
    const auth = useAuth();
    const navigate = useNavigate();
    const trackerMutation = useTrackerMutation({ diaryId });
    const updateTracker = useUpdateTracker({ diaryId });
    const { locale } = useAppLocale();
    const copy =
        locale === "fr-FR"
            ? {
                  addDataCollection: "Ajouter une collecte de données",
                  selectQuestionnaire: "Sélectionner un questionnaire",
                  patientQuestions: "À quelles questions le patient doit-il répondre ?",
                  selectSchedule: "Sélectionner un calendrier",
                  schedulePrompt: "Quand le patient doit-il remplir le questionnaire ?",
              }
            : locale === "it-IT"
              ? {
                    addDataCollection: "Aggiungi una raccolta dati",
                    selectQuestionnaire: "Seleziona un questionario",
                    patientQuestions: "A quali domande deve rispondere il paziente?",
                    selectSchedule: "Seleziona un calendario",
                    schedulePrompt: "Quando deve compilare il questionario il paziente?",
                }
              : locale === "es-ES"
                ? {
                      addDataCollection: "Añadir una recogida de datos",
                      selectQuestionnaire: "Seleccionar un cuestionario",
                      patientQuestions: "¿A qué preguntas debe responder el paciente?",
                      selectSchedule: "Seleccionar un calendario",
                      schedulePrompt: "¿Cuándo debe cumplimentar el cuestionario el paciente?",
                  }
                : locale === "de-DE"
                  ? {
                        addDataCollection: "Datenerfassung hinzufügen",
                        selectQuestionnaire: "Fragebogen auswählen",
                        patientQuestions: "Welche Fragen soll der Patient beantworten?",
                        selectSchedule: "Zeitplan auswählen",
                        schedulePrompt: "Wann soll der Patient den Fragebogen ausfüllen?",
                    }
                  : locale === "da-DK"
                    ? {
                          addDataCollection: "Tilføj dataindsamling",
                          selectQuestionnaire: "Vælg spørgeskema",
                          patientQuestions: "Hvilke spørgsmål skal patienten besvare?",
                          selectSchedule: "Vælg tidsplan",
                          schedulePrompt: "Hvornår skal patienten udfylde spørgeskemaet?",
                      }
                    : {
                          addDataCollection: "Add Data Collection",
                          selectQuestionnaire: "Select Questionnaire",
                          patientQuestions: "What questions should the patient answer?",
                          selectSchedule: "Select Schedule",
                          schedulePrompt: "When should the patient complete the questionnaire?",
                      };

    const form = useForm<{ form_id: string; schedule_id: string }>({
        resolver: zodResolver(templateSettingsSchema.shape.trackers.element),
        defaultValues: {
            form_id: "",
            schedule_id: "",
        },
    });

    async function onSubmit(data: { form_id: string; schedule_id: string }) {
        await trackerMutation.mutateAsync({
            diary_id: diaryId,
            tracker_id: trackerId,
            form_id: data.form_id,
            schedule_id: data.schedule_id,
        });

        // If we have an existing shared_attribute_id (coming from event completion), link directly
        if (existingSharedAttributeId) {
            updateTracker.mutate({
                tracker_id: trackerId,
                shared_attribute_id: existingSharedAttributeId,
            });
            navigate({ to: `/patients/${diaryId}` });
        } else {
            // Otherwise, go to the details page to select/create a shared attribute
            navigate({
                to: "/templates/diary/$diaryId/trackers/$trackerId/details",
                params: { diaryId, trackerId },
            });
        }
    }

    return (
        <c.ui.Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <c.ui.Card>
                    <c.ui.CardHeader>
                        <c.ui.CardTitle>{copy.addDataCollection}</c.ui.CardTitle>
                    </c.ui.CardHeader>
                    <c.ui.CardContent className="space-y-4">
                        <BuilderFormSelectDialog
                            name="form_id"
                            control={form.control}
                            options={filterBuilderPickerItems(
                                (forms ?? []).filter((f) => !!f.form_id),
                                { isAdmin: auth.roles.isAdmin, userId: auth.id }
                            ).map((f) => ({
                                ...f,
                                form_id: f.form_id as string,
                                form_name: f.form_name ?? "",
                                form_description: f.form_description ?? undefined,
                                prevalence: f.prevalence ?? undefined,
                                scoring: f.scoring ?? undefined,
                                time_to_complete: f.time_to_complete ?? undefined,
                                indications: f.indications ?? undefined,
                                orthopaedic_areas: f.orthopaedic_areas ?? undefined,
                                number_of_pages: f.number_of_pages ?? undefined,
                                number_of_questions: f.number_of_questions ?? undefined,
                                is_published: f.is_published ?? undefined,
                                author: f.author ?? undefined,
                            }))}
                            label={copy.selectQuestionnaire}
                            description={copy.patientQuestions}
                            locale={locale}
                        />
                        <BuilderScheduleSelectDialog
                            name="schedule_id"
                            control={form.control}
                            options={filterBuilderPickerItems(
                                (schedules ?? []).filter((s) => !!s.schedule_id),
                                { isAdmin: auth.roles.isAdmin, userId: auth.id }
                            ).map((s) => ({ ...s, schedule_id: s.schedule_id as string }))}
                            label={copy.selectSchedule}
                            description={copy.schedulePrompt}
                            locale={locale}
                        />
                    </c.ui.CardContent>
                </c.ui.Card>
                <NextButton />
            </form>
        </c.ui.Form>
    );
};

export const Route = createFileRoute("/_app/templates/diary/$diaryId/trackers/$trackerId/")({
    component: TrackerForm,
    validateSearch: searchSchema,
});
